<script lang="ts">
	import type { ComponentProps, Snippet } from 'svelte';
	import { Markdown, type MarkdownSource, type MarkdownTokenList } from '../Markdown';
	import { parseMarkdownSource } from '../Markdown/utils/parseMarkdownSource/parseMarkdownSource';

	type Props = Omit<ComponentProps<typeof Markdown>, 'source'> & {
		/**
		 * Markdown that may only exist in the browser (a toast, a cart entry, a
		 * page of results fetched after load), or tokens parsed on the server.
		 */
		source: string | MarkdownTokenList | null | undefined;
		/** Shown while a string waits for the parser (tokens-only builds). */
		pending?: Snippet;
	};

	let { source, pending, ...rest }: Props = $props();

	// In a tokens-only browser build (markdownTokensOnly) there is no parser up
	// front: a string here loads it on first use, and renders once it has
	// arrived. Everywhere else, and on the server, this is plain Markdown.
	const TOKENS_ONLY = import.meta.env.VITE_MARKDOWN_TOKENS_ONLY === 'true';
	const parseLater = !parseMarkdownSource;

	// A string rendered on the server has no tokens in the browser, so it is
	// parsed again after hydration and pops in: pass tokens for server content.
	// svelte-ignore state_referenced_locally
	if (import.meta.env.SSR && TOKENS_ONLY && typeof source === 'string') {
		console.warn('LazyMarkdown rendered a string on the server:', source.slice(0, 80));
	}

	let parsed = $state<{ source: string; tokens: MarkdownTokenList }>();

	$effect(() => {
		if (!parseLater || typeof source !== 'string') return;

		const pending = source;
		void import('../Markdown/utils/lexMarkdown/lexMarkdown').then(({ lexMarkdown }) => {
			if (source === pending) parsed = { source: pending, tokens: lexMarkdown(pending) };
		});
	});

	let tokens = $derived.by((): MarkdownSource | undefined => {
		if (typeof source !== 'string') return source ?? undefined;
		// A string is only a Markdown source where the build still parses.
		if (!parseLater) return source as unknown as MarkdownSource;
		return parsed?.source === source ? parsed.tokens : undefined;
	});
</script>

{#if tokens !== undefined}
	<Markdown source={tokens} {...rest} />
{:else if source}
	{@render pending?.()}
{/if}
